import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useOptimizedOrders,
  useAiOrdersMetrics,
} from "../../../hooks/useDashboardQueries";
import { useGlobalUtils } from "../../../lib/globalUtils";
import { StatsGrid } from "../../ui/Data/StatCard";
import { useOrderSocketUpdates } from "../../../hooks/useOrderSocketUpdates";
import { useUrlStates } from "@finitless/shared/hooks";
import { Icon } from "../../ui/UIElements/Icon";
import OrderCard from "../../ui/Orders/OrderCard";
import ConversationAiModal from "../../ui/Modals/ConversationAiModal";
import Pagination from "../../ui/Data/Pagination";
import OrdersLoading from "../../ui/Orders/OrdersLoading";
import OrdersError from "../../ui/Orders/OrdersError";
import {
  STATUS_LABELS,
  FILTER_TABS,
  ORDER_SEARCH_OPTIONS,
  ORDER_SEARCH_FIELDS,
  getStatusGroup,
  paramsToSend,
  ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  buildWhereClause,
  getOrdersDeliveryDatetimeRange,
} from "@finitless/shared/orderConstants";
import { PageHeader } from "@finitless/shared/components";
import { BusinessDropdown } from "../../ui/Business/BusinessDropdown";
import MonthYearPicker from "../reports/MonthYearPicker";
import { getCurrentMonth } from "../../../lib/reportUtils";

const OrdersPage = () => {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const { parsePrice } = useGlobalUtils();
  // Use URL state for persistence
  const [urlStates, updateUrlState] = useUrlStates({
    filter: "preparing",
    searchTerm: "",
    searchBy: ORDER_SEARCH_FIELDS.ORDER_ID,
    currentPage: "1",
  });

  const searchByFromUrl = ORDER_SEARCH_OPTIONS.some(
    (option) => option.value === urlStates.searchBy,
  )
    ? urlStates.searchBy
    : ORDER_SEARCH_FIELDS.ORDER_ID;

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [filter, setFilter] = useState(urlStates.filter || "preparing");
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [searchBy, setSearchBy] = useState(searchByFromUrl);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );

  // Local state for business filter (doesn't modify global context)
  const [filterBusiness, setFilterBusiness] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);

  const { smartPagination } = useOrderSocketUpdates({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleClearBusinessFilter = () => {
    setFilterBusiness(null);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  // Use filterBusiness if set, otherwise don't filter by business (null)
  const businessForFilter = filterBusiness || null;

  const deliveryDatetimeRange = useMemo(
    () => getOrdersDeliveryDatetimeRange(selectedDate),
    [selectedDate],
  );

  const { data: allOrdersData } = useOptimizedOrders({
    ...paramsToSend,
    where: buildWhereClause(
      "all",
      searchTerm,
      businessForFilter,
      searchBy,
      deliveryDatetimeRange,
    ),
  });

  const { data: preparingOrdersData } = useOptimizedOrders({
    ...paramsToSend,
    where: buildWhereClause(
      "preparing",
      searchTerm,
      businessForFilter,
      searchBy,
      deliveryDatetimeRange,
    ),
  });

  const { data: completedOrdersData } = useOptimizedOrders({
    ...paramsToSend,
    where: buildWhereClause(
      "completed",
      searchTerm,
      businessForFilter,
      searchBy,
      deliveryDatetimeRange,
    ),
  });

  const { data: cancelledOrdersData } = useOptimizedOrders({
    ...paramsToSend,
    where: buildWhereClause(
      "cancelled",
      searchTerm,
      businessForFilter,
      searchBy,
      deliveryDatetimeRange,
    ),
  });

  const {
    data: currentTabOrdersData,
    isLoading: currentTabLoading,
    error: currentTabError,
  } = useOptimizedOrders({
    ...paramsToSend,
    page: currentPage,
    where: buildWhereClause(
      filter,
      searchTerm,
      businessForFilter,
      searchBy,
      deliveryDatetimeRange,
    ),
  });

  const ordersMetricsParams = { business_id: filterBusiness?.id || null };
  if (selectedDate) {
    ordersMetricsParams.month = getMonthString(selectedDate);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      ordersMetricsParams.timezone = tz;
    } else {
      ordersMetricsParams.timezone_offset = -(
        new Date().getTimezoneOffset() / 60
      );
    }
  }

  const { data: aiOrdersMetricsData, isLoading: metricsLoading } =
    useAiOrdersMetrics(ordersMetricsParams);

  const orders = currentTabOrdersData?.orders || [];
  const paginationData = currentTabOrdersData?.pagination;

  // Calculate totalPages manually if not provided by API
  const calculatedTotalPages = paginationData?.total
    ? Math.ceil(paginationData.total / ITEMS_PER_PAGE)
    : 1;

  const totalPages = paginationData?.totalPages || calculatedTotalPages;

  const isLoading = currentTabLoading;
  const error = currentTabError;

  // Update smart pagination when data changes (for socket updates)
  useEffect(() => {
    if (paginationData && smartPagination) {
      smartPagination.updateTotalItems(paginationData);
    }
  }, [paginationData, smartPagination]);

  const tabCounts = {
    all: allOrdersData?.pagination?.total || 0,
    preparing: preparingOrdersData?.pagination?.total || 0,
    completed: completedOrdersData?.pagination?.total || 0,
    cancelled: cancelledOrdersData?.pagination?.total || 0,
  };

  const hasOrdersOnOtherTabs = FILTER_TABS.some(
    ({ key }) => key !== filter && (tabCounts[key] ?? 0) > 0,
  );

  // Pagination is now handled by smartPagination

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const sign = change >= 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };

  // AI Orders Metrics Cards based on the API response structure
  const metricCards = [
    {
      id: "ai-todays-orders",
      title: t("metrics.todaysOrders"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysOrders.ariaLabel"),
        whatIs: t("help.metrics.todaysOrders.whatIs"),
        period: t("help.metrics.todaysOrders.period"),
      },
      value: aiOrdersMetricsData?.total_orders?.current ?? 0,
      change: formatChangePercentage(
        aiOrdersMetricsData?.total_orders?.change_percentage,
      ),
      yesterday: aiOrdersMetricsData?.total_orders?.previous ?? 0,
      icon: "ShoppingBag",
      iconColor: "text-blue-400",
      iconBg: "bg-blue-500/10",
      gradient: true,
    },
    {
      id: "ai-revenue",
      title: t("metrics.todaysRevenue"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysRevenue.ariaLabel"),
        whatIs: t("help.metrics.todaysRevenue.whatIs"),
        period: t("help.metrics.todaysRevenue.period"),
      },
      value: parsePrice(aiOrdersMetricsData?.revenue?.total?.current ?? 0),
      change: formatChangePercentage(
        aiOrdersMetricsData?.revenue?.total?.change_percentage,
      ),
      yesterday: parsePrice(aiOrdersMetricsData?.revenue?.total?.previous ?? 0),
      icon: "DollarSign",
      iconColor: "text-green-400",
      iconBg: "bg-green-500/10",
      valueColor: "text-green-400",
    },
    {
      id: "ai-active-orders",
      title: t("metrics.todaysActive"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysActive.ariaLabel"),
        whatIs: t("help.metrics.todaysActive.whatIs"),
        period: t("help.metrics.todaysActive.period"),
      },
      value: aiOrdersMetricsData?.active_orders?.current ?? 0,
      change: formatChangePercentage(
        aiOrdersMetricsData?.active_orders?.change_percentage,
      ),
      yesterday: aiOrdersMetricsData?.active_orders?.previous ?? 0,
      icon: "Clock",
      iconColor: "text-yellow-400",
      iconBg: "bg-yellow-500/10",
      valueColor: "text-yellow-400",
    },
    {
      id: "ai-completed-orders",
      title: t("metrics.todaysCompleted"),
      titleHelp: {
        ariaLabel: t("help.metrics.todaysCompleted.ariaLabel"),
        whatIs: t("help.metrics.todaysCompleted.whatIs"),
        period: t("help.metrics.todaysCompleted.period"),
      },
      value: aiOrdersMetricsData?.completed_orders?.current ?? 0,
      change: formatChangePercentage(
        aiOrdersMetricsData?.completed_orders?.change_percentage,
      ),
      yesterday: aiOrdersMetricsData?.completed_orders?.previous ?? 0,
      icon: "Check",
      iconColor: "text-green-400",
      iconBg: "bg-green-500/10",
      valueColor: "text-green-400",
    },
  ];

  const getFilterCount = (filterKey) => {
    return tabCounts[filterKey] || 0;
  };

  const getFilterLabel = (filterKey, fallbackLabel) => {
    const labelByFilter = {
      preparing: t("tabs.active"),
      completed: t("tabs.completed"),
      cancelled: t("tabs.cancelled"),
      all: t("tabs.allOrders"),
    };

    return labelByFilter[filterKey] || fallbackLabel;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    updateUrlState("filter", newFilter);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleSearchByChange = (value) => {
    setSearchBy(value);
    setCurrentPage(1);
    updateUrlState("searchBy", value);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handleSelectedDateChange = (date) => {
    setSelectedDate(date);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleViewDetails = (order) => {
    // Pass the order data as state to avoid refetch
    navigate(`/orders/${order.id}`, { state: { order } });
  };

  const handleViewConversation = (order) => {
    setSelectedOrder(order);
    setShowConversationModal(true);
  };

  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
    setSelectedOrder(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <PageHeader
          title={
            filterBusiness
              ? t("header.titleWithBusiness", { name: filterBusiness.name })
              : t("header.title")
          }
          subtitle={t("header.subtitle")}
        />
        <div className="flex space-x-3">
          <a
            href="/download-app"
            className="flex items-center space-x-2 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition-colors hover:bg-gray-700 sm:px-4"
          >
            <Icon name="Download" className="h-4 w-4" />
            <span>{t("header.downloadApp")}</span>
          </a>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleSelectedDateChange(null)}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate === null
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.today", { ns: "common" })}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth(), 1),
                );
              }}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate &&
                selectedDate.getMonth() === new Date().getMonth() &&
                selectedDate.getFullYear() === new Date().getFullYear()
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.currentMonth", { ns: "common" })}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth() - 1, 1),
                );
              }}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate &&
                selectedDate.getMonth() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getMonth() &&
                selectedDate.getFullYear() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getFullYear()
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.lastMonth", { ns: "common" })}
            </button>
            <MonthYearPicker
              value={selectedDate}
              onChange={handleSelectedDateChange}
              placeholder={t("date.selectMonth", { ns: "common" })}
              id="orders-month-picker"
            />
          </div>
        </div>
      </div>
      {metricsLoading ? (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:mb-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse p-4 sm:p-6">
              <div className="mb-3 h-3 w-1/2 rounded bg-gray-700 sm:mb-4 sm:h-4"></div>
              <div className="h-6 w-3/4 rounded bg-gray-700 sm:h-8"></div>
            </div>
          ))}
        </div>
      ) : (
        <StatsGrid stats={metricCards} columns={4} className="mb-4 sm:mb-6" />
      )}

      {/* Status Tabs */}
      <div className="border-b border-gray-800">
        <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
          {FILTER_TABS.map(({ key, label, color }) => {
            const count = getFilterCount(key);
            return (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                  filter === key
                    ? `border-current ${color}`
                    : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                }`}
              >
                {getFilterLabel(key, label)} ({count})
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side - Business Filter and Search */}
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Business Filter Dropdown */}
            <div className="relative flex-shrink-0" ref={businessDropdownRef}>
              <button
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                className="hover:bg-gray-750 flex h-10 w-full min-w-[180px] items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm transition-colors sm:w-auto"
              >
                <span className="truncate text-gray-300">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
                />
              </button>
              {filterBusiness && (
                <button
                  onClick={handleClearBusinessFilter}
                  className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 transition-colors hover:bg-red-700"
                  title={t("filters.clearBusiness")}
                >
                  <Icon name="X" className="h-3 w-3 text-white" />
                </button>
              )}
              <div className="absolute top-full left-0 z-50 mt-2">
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>

            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="w-full sm:w-64">
                    <select
                      value={searchBy}
                      onChange={(e) => handleSearchByChange(e.target.value)}
                      aria-label={t("search.byLabel")}
                      className="custom-select block h-10 w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {ORDER_SEARCH_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative min-w-0 flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Icon name="Search" className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder={t("search.placeholder")}
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {searchInput && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                        type="button"
                      >
                        <svg
                          className="h-3 w-3 text-gray-300 hover:text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  {t("search.helpText", {
                    field: t(`search.fields.${searchBy}`),
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <OrdersLoading />
      ) : error ? (
        <OrdersError error={error} />
      ) : orders.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onViewDetails={handleViewDetails}
                onViewConversation={handleViewConversation}
                statusLabels={STATUS_LABELS}
                getStatusGroup={getStatusGroup}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="sticky bottom-0 z-10 mt-8 rounded-lg border border-gray-800 bg-gray-900 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-sm text-gray-400">
                {t("pagination.showing", {
                  from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * ITEMS_PER_PAGE,
                    paginationData?.total || 0,
                  ),
                  total: paginationData?.total || 0,
                })}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
                className="mt-4 sm:mt-0"
              />
            </div>
          </div>
        </>
      ) : (
        <div
          className={`mx-auto max-w-lg py-10 text-center ${
            hasOrdersOnOtherTabs
              ? "rounded-2xl border border-blue-500/15 bg-gradient-to-b from-gray-900/80 to-gray-950/40 px-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] sm:px-10"
              : ""
          }`}
        >
          <div
            className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${
              hasOrdersOnOtherTabs
                ? "bg-blue-500/10 ring-1 ring-blue-500/25"
                : "bg-transparent"
            }`}
          >
            <Icon
              name={hasOrdersOnOtherTabs ? "CalendarRange" : "ShoppingBag"}
              className={`h-8 w-8 ${
                hasOrdersOnOtherTabs ? "text-blue-400/90" : "text-gray-400"
              }`}
            />
          </div>
          <h3 className="text-base font-medium tracking-tight text-gray-200">
            {hasOrdersOnOtherTabs
              ? t("empty.titleNoOrdersInView")
              : t("empty.title")}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-gray-400">
            {hasOrdersOnOtherTabs
              ? t("empty.hintPeersHaveOrders")
              : searchTerm
                ? t("empty.subtitleSearch")
                : t("empty.subtitleDefault")}
          </p>
        </div>
      )}

      {/* Conversation Modal */}

      {showConversationModal && selectedOrder?.conversation_id && (
        <ConversationAiModal
          order={selectedOrder}
          isOpen={showConversationModal}
          onClose={handleCloseConversationModal}
        />
      )}
    </div>
  );
};

export default OrdersPage;
